<template>
  <div class="textures-grouped">
    <!-- Header -->
    <div class="toolbar toolbar-sticky" :style="{ top: toolbarOffset }">
      <div class="toolbar-left">
        <h1>Textures</h1>
        <p class="subtitle">Gegroepeerd op Texture Type • Sortering: naam (A-Z)</p>
      </div>
      <div class="toolbar-actions">
        <LoadingButton :disabled="!formDirty || loading" :loading="loading" @click="updateTextures">Opslaan</LoadingButton>
        <input id="searchInput" v-model.trim="search" type="text" placeholder="Zoeken…" class="input search-inline-input" />
      </div>
    </div>

    <!-- Hidden global file input (trigger via knop in groups of aparte actie indien gewenst) -->
  <input ref="globalFileInput" type="file" accept="image/*" class="file-hidden" @change="handleGlobalAddFile" />

    <!-- Groups -->
    <div class="groups">
      <!-- Create Modal -->
      <div v-if="showCreateModal" class="modal-overlay" @click.self="cancelCreateModal">
        <div class="modal">
          <div class="modal-header">
            <h3>Nieuwe texture</h3>
          </div>
          <div class="modal-body">
            <div class="modal-grid">
              <div class="modal-image" @dragover.prevent @drop.prevent="onDraftDropReplace($event)">
                <div class="card-image-wrapper" @click="triggerDraftReplace">
                  <img v-if="draftDisplayUrl" :src="draftDisplayUrl" alt="Preview" class="card-image" />
                  <div v-else class="placeholder">Afbeelding verplicht — klik of sleep een bestand</div>
                  <div class="image-overlay">
                    <mdicon name="upload" size="42" />
                    <span>Upload</span>
                  </div>
                  <input ref="draftFileInput" type="file" accept="image/*" class="file-hidden" @change="onDraftReplaceFile" />
                </div>
              </div>
              <div class="modal-fields">
                <div class="field">
                  <label for="draft-name">Naam</label>
                  <input id="draft-name" v-model.trim="draftTexture.name" type="text" class="input" placeholder="Naam" />
                </div>
                <div class="field">
                  <label for="draft-type">Texture type</label>
                  <select id="draft-type" v-model="draftTexture.textureType" class="input select">
                    <option :value="null" disabled>-- kies type --</option>
                    <option v-for="t in textureTypes" :key="t.id" :value="t.id">{{ t.name }}</option>
                  </select>
                </div>
                <div class="field" v-if="isPlankTexture(draftTexture)">
                  <label for="draft-plank">Plank type</label>
                  <select id="draft-plank" v-model="draftTexture.plankType" class="input select">
                    <option :value="null" disabled>-- kies plank --</option>
                    <option v-for="p in plankTypes" :key="p.id" :value="p.id">{{ p.name }}</option>
                  </select>
                </div>
                <div class="field">
                  <label for="draft-color">Kleur</label>
                  <select id="draft-color" v-model="draftTexture.color" class="input select">
                    <option :value="null" disabled>-- kies kleur --</option>
                    <option v-for="c in colors" :key="c.id" :value="c.id">{{ c.name }}</option>
                  </select>
                </div>
                <div class="field" v-if="isPlankTexture(draftTexture)">
                  <label for="draft-hout">Houtsoort</label>
                  <select id="draft-hout" v-model="draftTexture.houtsoort" class="input select">
                    <option :value="null" disabled>-- kies houtsoort --</option>
                    <option v-for="h in houtsoorten" :key="h.id" :value="h.id">{{ h.name }}</option>
                  </select>
                </div>
                <div class="field locations" v-if="isPlankTexture(draftTexture)">
                  <label>Locaties</label>
                  <div class="locations-grid">
                    <label v-for="loc in plankLocations" :key="loc.id" class="checkbox-item">
                      <input type="checkbox" :value="loc.id" :checked="draftTexture.locations?.includes(loc.id)" @change="toggleDraftLocation(loc.id)" />
                      <span>{{ loc.name }}</span>
                    </label>
                  </div>
                </div>
              </div>
            </div>
          </div>
          <div class="modal-footer">
            <button type="button" class="btn" @click="cancelCreateModal">Annuleren</button>
            <LoadingButton :disabled="!canSaveDraft || loading" :loading="loading" @click="saveCreateModal">Opslaan</LoadingButton>
          </div>
        </div>
      </div>
      <details v-for="group in displayedGroups" :key="group.id" open class="group">
        <summary class="group-header" title="Klik om in/uit te klappen">
          <h2 class="group-title">
            <mdicon name="chevron-right" size="18" class="chevron" />
            <span class="title-text">{{ group.name }}</span>
            <span class="count">({{ group.textures.length }})</span>
          </h2>
          <div class="group-actions">
            <button type="button" class="btn btn-small" @click.stop="addTextureToGroup(group.id)">
              <mdicon name="plus" size="16" />
              <span>Nieuwe in groep</span>
            </button>
          </div>
        </summary>

        <div class="cards">
          <div v-for="(texture, idx) in group.textures" :key="texture.localUid" :class="['card', { 'card-dirty': isTextureDirty(texture), 'card-pending-move': isPendingTypeChange(texture), 'card-deleted': texture.__markedForDeletion }]" @dragover.prevent @drop.prevent="onDropReplace($event, texture)">
            <div v-if="isPendingTypeChange(texture)" class="badge-pending" :title="`Verplaatst naar '${textureTypeName(texture.textureType)}' na opslaan`">Type gewijzigd</div>
            <div v-if="texture.__markedForDeletion" class="badge-delete" title="Wordt verwijderd na opslaan">Verwijderen</div>
            <div class="card-image-wrapper" @click="triggerReplace(texture)">
              <!-- Open image in new tab button -->
              <button
                v-if="textureDisplayUrl(texture)"
                type="button"
                class="image-open-btn"
                title="Open afbeelding in nieuw tabblad"
                @click.stop="openImageInNewTab(texture)"
              >
                <mdicon name="open-in-new" size="18" />
              </button>
              <img
                v-if="textureDisplayUrl(texture)"
                :src="textureDisplayUrl(texture)"
                :alt="texture.name || 'Texture afbeelding'"
                class="card-image"
                @error="onImageError($event, texture)"
                @load="onImageLoad($event, texture)"
              />
              <div v-else class="placeholder">Geen afbeelding</div>
              <div class="image-overlay">
                <mdicon name="upload" size="42" />
                <span>Vervang / Upload</span>
              </div>
              <input ref="replaceInputs" type="file" accept="image/*" class="file-hidden" @change="file => onReplaceFile(file, texture)" />
            </div>

            <div class="fields">
              <div class="field">
                <label :for="`name-${texture.localUid}`">Naam</label>
                <input :id="`name-${texture.localUid}`" v-model.trim="texture.name" type="text" class="input" placeholder="Naam" />
              </div>

              <div class="field">
                <label :for="`type-${texture.localUid}`">Texture type</label>
                <select :id="`type-${texture.localUid}`" v-model="texture.textureType" class="input select">
                  <option :value="null" disabled>-- kies type --</option>
                  <option v-for="t in textureTypes" :key="t.id" :value="t.id">{{ t.name }}</option>
                </select>
              </div>

              <div class="field" v-if="isPlankTexture(texture)">
                <label :for="`plank-${texture.localUid}`">Plank type</label>
                <select :id="`plank-${texture.localUid}`" v-model="texture.plankType" class="input select">
                  <option :value="null" disabled>-- kies plank --</option>
                  <option v-for="p in plankTypes" :key="p.id" :value="p.id">{{ p.name }}</option>
                </select>
              </div>

              <div class="field">
                <label :for="`color-${texture.localUid}`">Kleur</label>
                <select :id="`color-${texture.localUid}`" v-model="texture.color" class="input select">
                  <option :value="null" disabled>-- kies kleur --</option>
                  <option v-for="c in colors" :key="c.id" :value="c.id">{{ c.name }}</option>
                </select>
              </div>

              <div class="field" v-if="isPlankTexture(texture)">
                <label :for="`hout-${texture.localUid}`">Houtsoort</label>
                <select :id="`hout-${texture.localUid}`" v-model="texture.houtsoort" class="input select">
                  <option :value="null" disabled>-- kies houtsoort --</option>
                  <option v-for="h in houtsoorten" :key="h.id" :value="h.id">{{ h.name }}</option>
                </select>
              </div>

              <div class="field locations" v-if="isPlankTexture(texture)">
                <label>Locaties</label>
                <div class="locations-grid">
                  <label v-for="loc in plankLocations" :key="loc.id" class="checkbox-item">
                    <input type="checkbox" :value="loc.id" :checked="texture.locations?.includes(loc.id)" @change="toggleLocation(texture, loc.id)" />
                    <span>{{ loc.name }}</span>
                  </label>
                </div>
              </div>
            </div>

            <div class="card-footer">
              <div class="footer-left">
                <button v-if="isTextureDirty(texture)" type="button" class="btn btn-small" @click="cancelChanges(texture)" title="Maak wijzigingen ongedaan">
                  Annuleren
                </button>
              </div>
              <div class="footer-right">
                <button v-if="!texture.__markedForDeletion" type="button" class="btn btn-danger btn-small" @click="removeTexture(texture)">
                  <mdicon name="delete" size="16" />
                  Verwijderen
                </button>
                <button v-else type="button" class="btn btn-small" @click="removeTexture(texture)">
                  Herstellen
                </button>
              </div>
            </div>
          </div>

          <!-- Add card in group -->
          <div class="card card-add" @click="addTextureToGroup(group.id)">
            <div class="add-inner">
              <mdicon name="plus" size="48" />
              <span>Nieuwe texture</span>
            </div>
          </div>
        </div>
      </details>
    </div>

  </div>
</template>

<script>
import isEqual from 'lodash.isequal'
import { mapGetters } from 'vuex'
import LoadingButton from '@/components/inputs/buttons/LoadingButton.vue'

// Zelfde speciale ID als in originele component voor plank textures
const PLANK_TEXTURE_TYPE_ID = '62cd857a7c20bc17b85af2cd'

let localUidCounter = 0
const nextLocalUid = () => ++localUidCounter
let orderCounter = 0

export default {
  name: 'TexturesGrouped',
  components: { LoadingButton },
  data () {
    return {
      search: '',
      texturesLocal: [], // diepe kopie van store textures
      textureTypes: [],
      plankTypes: [],
      colors: [],
      plankLocations: [],
      houtsoorten: [],
      loading: false,
      previewsServer: import.meta.env.VITE_VUE_APP_SERVER,
  toolbarOffset: '47px',
      showCreateModal: false,
      draftTexture: null
    }
  },
  computed: {
    ...mapGetters({
      texturesStore: 'getTextures',
      textureTypesM: 'getTextureTypes',
      plankTypesM: 'getPlankTypes',
      colorsM: 'getMenuColors',
      plankLocationsM: 'getPlankLocations',
      houtsoortenM: 'getHoutsoortTypes'
    }),

    formDirty () {
      return !isEqual(
        this.normalizeForCompare(this.texturesLocal),
        this.normalizeForCompare(this.texturesStore)
      )
    },

    textureTypeMap () {
      const map = {}
      this.textureTypes.forEach(t => { map[t.id] = t })
      return map
    },

    originalMap () {
      const map = {}
      if (Array.isArray(this.texturesStore)) {
        this.texturesStore.forEach(o => { if (o && o.id) map[o.id] = o })
      }
      return map
    },

    filteredTextures () {
      if (!this.search) return this.texturesLocal
      const q = this.search.toLowerCase()
      return this.texturesLocal.filter(t => (t.name || '').toLowerCase().includes(q))
    },

    grouped () {
      const groups = {}
      this.filteredTextures.forEach(t => {
        const effectiveType = t.id ? (t.__originalTextureType ?? t.textureType) : t.textureType
        const typeId = effectiveType || '___missing'
        if (!groups[typeId]) {
          groups[typeId] = {
            id: typeId,
            name: this.textureTypeMap[typeId]?.name || 'Onbekend type',
            textures: []
          }
        }
        groups[typeId].textures.push(t)
      })
      Object.values(groups).forEach(g => {
        g.textures.sort((a, b) => {
          const aName = ((a.__originalName ?? a.name) || '').trim()
          const bName = ((b.__originalName ?? b.name) || '').trim()
            // Beide leeg: sorteer op __order
          if (!aName && !bName) return (a.__order||0) - (b.__order||0)
          // Alleen a leeg => a naar onder (return 1)
          if (!aName) return 1
          // Alleen b leeg => b naar onder
          if (!bName) return -1
          const nameCmp = aName.localeCompare(bName, 'nl', { sensitivity: 'base' })
          if (nameCmp !== 0) return nameCmp
          return (a.__order||0) - (b.__order||0)
        })
      })
      return groups
    },

    displayedGroups () {
      return Object.values(this.grouped).sort((a, b) => a.name.localeCompare(b.name, 'nl', { sensitivity: 'base' }))
    },

    textureServerUrl () {
      const envServer = import.meta.env.VITE_VUE_APP_SERVER
      if (typeof window !== 'undefined' && window.location.hostname === 'localhost') {
        return 'http://localhost:8080'
      }
      return envServer || 'http://localhost:8080'
    },
    draftDisplayUrl() {
      const t = this.draftTexture
      if (!t) return null
      if (typeof t.tempImageStorage === 'string') return t.tempImageStorage
      if (t?.tempImageStorage instanceof File) return t._objectUrl || (t._objectUrl = URL.createObjectURL(t.tempImageStorage))
      return null
    },
    canSaveDraft() {
      const t = this.draftTexture
      if (!t) return false
      const hasName = !!(t.name && t.name.trim().length > 0)
      const hasType = !!t.textureType
      const hasImage = !!t.tempImageStorage
      return hasName && hasType && hasImage
    }
  },
  mounted() {
    // vaste offset 47px, geen dynamic resize nodig
  },
  beforeUnmount() {},
  methods: {
    measureToolbarOffset() {},
    triggerDraftReplace() { this.$refs.draftFileInput?.click() },
    onDraftReplaceFile(e) {
      const file = e.target.files?.[0]; if (!file) return
      const reader = new FileReader()
      reader.readAsDataURL(file)
      reader.onload = () => {
        this.draftTexture.tempImageStorage = reader.result
        e.target.value = ''
      }
    },
    onDraftDropReplace(evt) {
      const file = evt.dataTransfer.files?.[0]; if (!file) return
      const reader = new FileReader()
      reader.readAsDataURL(file)
      reader.onload = () => { this.draftTexture.tempImageStorage = reader.result }
    },
    toggleDraftLocation(id) {
      this.draftTexture.locations = this.draftTexture.locations || []
      const idx = this.draftTexture.locations.indexOf(id)
      if (idx >= 0) this.draftTexture.locations.splice(idx, 1)
      else this.draftTexture.locations.push(id)
    },
    isTextureDirty(texture) {
      // Nieuwe zonder wijzigingen? check
      if (!texture.id) {
        const hasSomething = (
          (texture.name && texture.name.trim() !== '') ||
          texture.textureType || texture.plankType || texture.color || texture.houtsoort ||
          (Array.isArray(texture.locations) && texture.locations.length > 0) ||
          texture.tempImageStorage ||
          texture.__markedForDeletion
        )
        return hasSomething
      }
      const original = this.originalMap[texture.id]
      if (!original) return true
      const changed = (
        texture.name !== original.name ||
        texture.textureType !== original.textureType ||
        texture.plankType !== original.plankType ||
        texture.color !== original.color ||
        texture.houtsoort !== original.houtsoort ||
        JSON.stringify([...(texture.locations||[])].sort()) !== JSON.stringify([...(original.locations||[])].sort()) ||
        !!texture.tempImageStorage ||
        !!texture.__markedForDeletion
      )
      return changed
    },
    isPendingTypeChange(texture) {
      if (!texture.id) return false
      return texture.__originalTextureType !== undefined && texture.textureType !== texture.__originalTextureType
    },
    textureTypeName(id) {
      return this.textureTypeMap[id]?.name || 'Onbekend type'
    },
    // Normaliseer voor vergelijking (negeer localUid en tempImageStorage raw object refs)
    normalizeForCompare(list) {
  if (!Array.isArray(list)) return []
  return list.map(t => ({
        id: t.id || null,
        name: t.name || '',
        textureType: t.textureType || null,
        plankType: t.plankType || null,
        color: t.color || null,
        houtsoort: t.houtsoort || null,
        locations: Array.isArray(t.locations) ? [...t.locations].sort() : [],
        // Bewaar indicatie van gewijzigde file via boolean
  hasNewFile: !!t.tempImageStorage,
  deleted: !!t.__markedForDeletion
      })).sort((a,b) => (a.id||'').localeCompare(b.id||''))
    },

    async primeData () {
      await Promise.all([
        this.$store.dispatch('getTextures'),
        this.$store.dispatch('getTextureTypes'),
        this.$store.dispatch('getPlankTypes'),
        this.$store.dispatch('getMenuColors'),
        this.$store.dispatch('getPlankLocations'),
        this.$store.dispatch('getHoutsoortTypes')
      ])
      // Diepe kopie + localUid
      this.texturesLocal = JSON.parse(JSON.stringify(this.texturesStore || [])).map((t, idx) => ({
        ...t,
        __originalTextureType: t.textureType,
        __originalName: t.name,
        localUid: nextLocalUid(),
        __order: ++orderCounter,
  tempImageStorage: null, // nieuw upload buffer
  __markedForDeletion: false
      }))
      this.textureTypes = JSON.parse(JSON.stringify(this.textureTypesM || []))
      this.plankTypes = JSON.parse(JSON.stringify(this.plankTypesM || []))
      this.colors = JSON.parse(JSON.stringify(this.colorsM || []))
      this.plankLocations = JSON.parse(JSON.stringify(this.plankLocationsM || []))
      this.houtsoorten = JSON.parse(JSON.stringify(this.houtsoortenM || []))
    },

    isPlankTexture (texture) {
      return texture.textureType === PLANK_TEXTURE_TYPE_ID
    },

    textureDisplayUrl (texture) {
      if (typeof texture.tempImageStorage === 'string') return texture.tempImageStorage
      if (texture.tempImageStorage instanceof File) {
        return texture._objectUrl || (texture._objectUrl = URL.createObjectURL(texture.tempImageStorage))
      }
      if (texture.url) return `${this.textureServerUrl}/textures/${texture.url}`
      return null
    },

    triggerAddTexture () {
      this.$refs.globalFileInput.click()
    },
    handleGlobalAddFile (e) {
      const file = e.target.files?.[0]
      if (!file) return
      const base = this.newTextureBase()
      const reader = new FileReader()
      reader.readAsDataURL(file)
      reader.onload = () => {
        base.tempImageStorage = reader.result
        this.texturesLocal.push(base)
        e.target.value = ''
      }
    },

    addTextureToGroup (typeId) {
      this.draftTexture = this.newTextureBase()
      this.draftTexture.textureType = typeId !== '___missing' ? typeId : null
  this.draftTexture.__originalName = this.draftTexture.name
      this.showCreateModal = true
    },
    cancelCreateModal() {
      this.showCreateModal = false
      this.draftTexture = null
    },
    saveCreateModal() {
      if (!this.canSaveDraft) return
      // push cloned draft as new entry
      const clone = JSON.parse(JSON.stringify(this.draftTexture))
  if (clone.__originalName === undefined) clone.__originalName = clone.name || ''
      this.texturesLocal.push(clone)
      this.showCreateModal = false
      this.draftTexture = null
    },

  newTextureBase () {
      return {
        id: null,
        localUid: nextLocalUid(),
        name: '',
  __originalName: '',
        url: '',
        textureType: null,
        __originalTextureType: null,
        plankType: null,
        color: null,
        houtsoort: null,
        locations: [],
  tempImageStorage: null,
  __order: ++orderCounter,
  __markedForDeletion: false
      }
    },

    triggerReplace (texture) {
      // zoek eerste file input binnen card (replaceInputs refs gedeeld)
      const card = this.$el.querySelector(`.card [id='name-${texture.localUid}']`)?.closest('.card')
      if (!card) return
      const input = card.querySelector('input[type=file]')
      input?.click()
    },

    onReplaceFile (e, texture) {
      const file = e.target.files?.[0]
      if (!file) return
      const reader = new FileReader()
      reader.readAsDataURL(file)
      reader.onload = () => {
        texture.tempImageStorage = reader.result // base64 data URL
        this.$forceUpdate()
        e.target.value = ''
      }
    },

    onDropReplace (evt, texture) {
      const file = evt.dataTransfer.files?.[0]
      if (!file) return
      const reader = new FileReader()
      reader.readAsDataURL(file)
      reader.onload = () => {
        texture.tempImageStorage = reader.result // base64 data URL
        this.$forceUpdate()
      }
    },

    toggleLocation (texture, locationId) {
      texture.locations = texture.locations || []
      const idx = texture.locations.indexOf(locationId)
      if (idx >= 0) texture.locations.splice(idx, 1)
      else texture.locations.push(locationId)
    },

    removeTexture (texture) {
      // Soft-delete toggle
      if (!texture.id) {
        // New unsaved item: toggle deletion marks, and filter out on save
        texture.__markedForDeletion = !texture.__markedForDeletion
        return
      }
      texture.__markedForDeletion = !texture.__markedForDeletion
    },

    cancelChanges (texture) {
      // Nieuwe kaart zonder id: volledig verwijderen (nog niet opgeslagen)
      if (!texture.id) {
        const idx = this.texturesLocal.findIndex(t => t.localUid === texture.localUid)
        if (idx >= 0) this.texturesLocal.splice(idx, 1)
        return
      }
      const original = this.originalMap[texture.id]
      if (!original) return
      // Velden terugzetten
      texture.name = original.name
      texture.textureType = original.textureType
      texture.plankType = original.plankType
      texture.color = original.color
      texture.houtsoort = original.houtsoort
      texture.locations = Array.isArray(original.locations) ? [...original.locations] : []
      // temp image resetten
      texture.tempImageStorage = null
      // soft-delete terugdraaien
      texture.__markedForDeletion = false
      // pending move badge weg (herstel originele type-tracker)
      texture.__originalTextureType = original.textureType
      // naam-sortering baseren we op oorspronkelijke naam tot save
      texture.__originalName = original.name
    },

    // Originele diff logic geherstructureerd
    async updateTextures () {
      if (!this.formDirty) return
      this.loading = true
  try {
  const original = Array.isArray(this.texturesStore) ? this.texturesStore : []
        const current = this.texturesLocal
        const toRemove = []
        const toUpdate = []
        const toInsert = []

        // Verwijderde
        original.forEach(o => {
          // removal occurs if it's missing in current OR marked for deletion
          const cur = current.find(c => c.id === o.id)
          if (!cur || cur.__markedForDeletion) toRemove.push(o)
        })

        // Toegevoegd (geen id)
        current.forEach(c => {
          if (!c.id && !c.__markedForDeletion) toInsert.push(c)
        })

        // Gewijzigd
        original.forEach(o => {
          const cur = current.find(c => c.id === o.id)
          if (!cur) return
          if (cur.__markedForDeletion) return // don't update if being removed
          const changed = (
            cur.name !== o.name ||
            cur.textureType !== o.textureType ||
            cur.plankType !== o.plankType ||
            cur.color !== o.color ||
            cur.houtsoort !== o.houtsoort ||
            JSON.stringify([...(cur.locations||[])].sort()) !== JSON.stringify([...(o.locations||[])].sort()) ||
            cur.tempImageStorage
          )
          if (changed) toUpdate.push(cur)
        })

        // Guard: prevent inserts without image (API requires base64 data URL)
        const invalidInserts = toInsert.filter(x => !x.tempImageStorage || (typeof x.tempImageStorage === 'string' && !x.tempImageStorage.startsWith('data:')))
        if (invalidInserts.length > 0) {
          this.loading = false
          alert('Upload een afbeelding voor alle nieuwe textures (afbeelding is verplicht).')
          return
        }

        await this.$store.dispatch('updateTextures', {
          remove: toRemove,
            update: toUpdate,
            insert: toInsert
        })
        // Herladen
        await this.$store.dispatch('getTextures')
        this.texturesLocal = JSON.parse(JSON.stringify(this.texturesStore)).map(t => ({
          ...t,
          __originalTextureType: t.textureType,
          __originalName: t.name,
          localUid: nextLocalUid(),
          __order: ++orderCounter,
          tempImageStorage: null,
          __markedForDeletion: false
        }))
      } catch (e) {
        console.error(e)
      } finally {
        this.loading = false
      }
    },

    // Image events
    onImageError (ev, texture) {
      ev.target.classList.add('img-error')
    },
    onImageLoad (ev, texture) {
      ev.target.classList.remove('img-error')
    },
    openImageInNewTab (texture) {
      const url = this.textureDisplayUrl(texture)
      if (!url) return
      // Open in a new tab without affecting the replace handler
      window.open(url, '_blank', 'noopener')
    }
  },
  created () {
    this.primeData()
  }
}
</script>

<style lang="scss" scoped>
.textures-grouped {
  padding: 0 28px 60px; /* geen top-gap zodat header tegen global header zit */
  max-width: 1600px;
  margin: 0 auto;
  display: flex;
  flex-direction: column;
  gap: 24px;
}

.toolbar {
  display: flex;
  align-items: center;
  justify-content: space-between;
  flex-wrap: nowrap;
  gap: 24px;
  background: #fff;
  padding: 12px 20px;
  border: 1px solid #e2e6ea;
  border-radius: $radius-m-1;
  box-shadow: $shadow-m-0;
  h1 { margin: 0; font-size: 1.6rem; line-height: 1.1; font-weight: 700; color: $c-primary; }
  .subtitle { margin: 2px 0 0; font-size: .8rem; color: $c-sub-text; font-weight: 500; letter-spacing: .5px; }
}

.toolbar-sticky { position: sticky; top: var(--toolbar-offset, 70px); z-index: 120; margin-top: 0; border-radius: 0 0 8px 8px; }
.toolbar-actions { display: flex; align-items: center; gap: 12px; }
.search-inline-input { width: 260px; background: $c-background-1; border: 1px solid #e2e6ea; }

.filter-bar {
  display: flex;
  flex-wrap: wrap;
  gap: 24px;
  background: #fff;
  padding: 16px 20px;
  border-radius: 8px;
  box-shadow: 0 2px 4px rgba(0,0,0,.05);
}

.filter-field { display: flex; flex-direction: column; gap: 6px; min-width: 220px; }
label { font-size: .75rem; font-weight: 600; letter-spacing: .5px; text-transform: uppercase; color: #555; }

.input { border: 1px solid #e2e6ea; border-radius: 6px; padding: 6px 10px; font-size: .9rem; background: $c-background-1; transition: border-color .15s, background .15s; width: 100%; box-sizing: border-box; }
.card .fields .field .input, .card .fields .field select.input { width: 100%; }
.input:focus { outline: none; border-color: $c-secondary; background: #fff; }
select.input { padding: 6px 8px; }

.btn { border: 1px solid #e2e6ea; background: #fff; color: #222; border-radius: 6px; padding: 6px 12px; font-size: .8rem; font-weight: 500; cursor: pointer; display: inline-flex; align-items: center; gap: 6px; transition: background .15s, border-color .15s, box-shadow .15s, transform .15s; box-shadow: $shadow-m-0; }
.btn:hover { background: hsl(0,0%,0%,.08); box-shadow: $shadow-m-1; transform: translateY(-1px); }
.btn:disabled { opacity: .5; cursor: not-allowed; }
.btn-add { border-color: #2f7bff; color: #2f7bff; background: #eef5ff; }
.btn-add:hover { background: #d9e9ff; }
.btn-small { padding: 4px 10px; font-size: .75rem; }
.btn-danger { border-color: #d63939; color: #d63939; background: #fff2f2; }
.btn-danger:hover { background: #ffe2e2; }

.groups { display: flex; flex-direction: column; gap: 28px; }
.group { 
  background: linear-gradient(135deg, #f8f9ff 0%, #fff5e6 100%);
  border-radius: $radius-m-1; 
  border: 2px solid #babc16; 
  padding: 8px 0 16px; 
  box-shadow: 0 4px 12px rgba(186, 188, 22, 0.1);
}
.group[open] { animation: fadeIn .25s ease; }
@keyframes fadeIn { from { opacity: 0; transform: translateY(-4px); } to { opacity: 1; transform: translateY(0); } }
.group-header { list-style: none; cursor: pointer; display: flex; align-items: center; justify-content: space-between; gap: 12px; padding: 8px 20px 12px; border-bottom: 2px solid $c-secondary; }
.group-title { margin: 0; font-size: 1.1rem; display: flex; align-items: center; gap: 10px; color: #babc16; font-weight: 700; }
.group .chevron { transition: transform .2s ease; color: inherit; }
.group[open] .chevron { transform: rotate(90deg); }
.group .title-text { display: inline-block; }
.count { font-size: .8rem; font-weight: 500; color: #666; }
.group-actions { display: flex; gap: 8px; }

.cards { display: grid; grid-template-columns: repeat(auto-fill, minmax(270px, 1fr)); gap: 18px; margin: 20px 20px 0; }
.card { position: relative; display: flex; flex-direction: column; gap: 10px; border: 1px solid #e2e6ea; background: #fff; border-radius: $radius-m-1; padding: 14px 14px 12px; min-height: 420px; box-shadow: $shadow-m-0; transition: box-shadow .15s, border-color .15s, transform .15s; }
.card:hover { background-color: #fff; border-color: #cbd5e1; transform: translateY(-1px); box-shadow: $shadow-m-1; }
.card-dirty { background: #fff9ed; border-color: #f3b95c; box-shadow: 0 0 0 2px rgba(243,185,92,.25); }
/* Override generic hover: keep dirty color and accentuate outline */
.card-dirty:hover { background: #fff9ed; border-color: #e99f27; box-shadow: 0 0 0 3px rgba(233,159,39,.35); transform: translateY(-1px); }
.card-pending-move { position: relative; }
.badge-pending { position: absolute; top: 6px; right: 6px; background: #1d4ed8; color: #fff; font-size: 10px; font-weight: 600; padding: 3px 6px; border-radius: 4px; letter-spacing: .5px; box-shadow: 0 2px 4px rgba(0,0,0,.15); z-index: 3; }
.card-deleted { background: #fff1f1; border-color: #d63939; opacity: .9; }
/* Override generic hover: keep deleted color and accentuate outline */
.card-deleted:hover { background: #fff1f1; border-color: #d63939; box-shadow: 0 0 0 3px rgba(214,57,57,.30); transform: translateY(-1px); }
.card-deleted .image-overlay { background: linear-gradient(180deg,rgba(214,57,57,0.05),rgba(214,57,57,.4)); }
.badge-delete { position: absolute; top: 6px; left: 6px; background: #d63939; color: #fff; font-size: 10px; font-weight: 700; padding: 3px 6px; border-radius: 4px; letter-spacing: .5px; box-shadow: 0 2px 4px rgba(0,0,0,.15); z-index: 3; }
.card-image-wrapper { position: relative; border: 1px solid #e2e6ea; border-radius: $radius-m-1; aspect-ratio: 4/3; overflow: hidden; cursor: pointer; background: $c-background-1; }
.card-image { width: 100%; height: 100%; object-fit: contain; background: #fff; }
.image-open-btn { position: absolute; top: 6px; right: 6px; z-index: 4; border: 1px solid #e2e6ea; background: rgba(255,255,255,.95); color: #1f2937; border-radius: 6px; padding: 4px; display: inline-flex; align-items: center; justify-content: center; box-shadow: 0 1px 2px rgba(0,0,0,.08); }
.image-open-btn:hover { background: #fff; }
.placeholder { display: flex; align-items: center; justify-content: center; font-size: .8rem; color: #666; height: 100%; }
.image-overlay { position: absolute; inset: 0; background: linear-gradient(180deg,rgba(0,0,0,0.0),rgba(0,0,0,.55)); display: flex; flex-direction: column; align-items: center; justify-content: flex-end; gap: 4px; padding-bottom: 10px; color: #fff; font-size: .65rem; opacity: 0; transition: opacity .2s; font-weight: 600; letter-spacing: .5px; text-transform: uppercase; }
.card-image-wrapper:hover .image-overlay { opacity: 1; }
.img-error { outline: 2px solid #d63939; }

.fields { display: flex; flex-direction: column; gap: 10px; }
.field { display: flex; flex-direction: column; gap: 4px; width: 100%; }
.locations .locations-grid { display: flex; flex-wrap: wrap; gap: 4px 8px; }
.checkbox-item { display: flex; align-items: center; gap: 4px; font-size: .65rem; background: #eef2f5; padding: 3px 6px; border-radius: 4px; cursor: pointer; }
.checkbox-item input { margin: 0; }

.card-footer { margin-top: auto; display: flex; justify-content: space-between; align-items: center; gap: 8px; }
.card-footer .footer-left, .card-footer .footer-right { display: flex; align-items: center; gap: 8px; }

.card-add { align-items: center; justify-content: center; background: #f1f6ff; border: 1px dashed #2f7bff; cursor: pointer; min-height: 200px; border-radius: $radius-m-1; }
.card-add:hover { background: #e2f0ff; transform: translateY(-1px); box-shadow: $shadow-m-1; }
.add-inner { display: flex; flex-direction: column; align-items: center; gap: 6px; color: #2f7bff; font-weight: 600; font-size: .8rem; }

/* footer-save verwijderd: save knop zit nu in fixed header */

.file-hidden { display: none; }

/* Verwijderd 2-koloms layout: alle dropdowns onder elkaar voor consistentie */

/* Modal */
.modal-overlay { position: fixed; inset: 0; background: rgba(0,0,0,.35); display: flex; align-items: center; justify-content: center; z-index: 400; }
.modal { background: #fff; border-radius: $radius-m-1; width: min(900px, 92vw); max-height: 90vh; overflow: hidden; box-shadow: $shadow-m-1; display: flex; flex-direction: column; border: 1px solid #e2e6ea; }
.modal-header { padding: 14px 18px; border-bottom: 1px solid #e2e6ea; background: $c-background-1; }
.modal-header h3 { margin: 0; font-size: 1.1rem; font-weight: 700; color: $c-primary; }
.modal-body { padding: 16px 18px; overflow: auto; background: #fff; }
.modal-footer { padding: 12px 18px; border-top: 1px solid #e2e6ea; display: flex; justify-content: flex-end; gap: 10px; background: #fff; }
.modal-grid { display: grid; grid-template-columns: 1fr 1.2fr; gap: 16px; }
.modal-fields { display: flex; flex-direction: column; gap: 10px; }
</style>